/**
 * @author       tae@zumper.com (Tae Yoon)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  TourButton
 */

/** @jsx jsx */
import { type Interpolation, type Theme, jsx } from '@emotion/react'
import buttonCss from '@zcode-ui/web-zumper-features/src/ListingCard/buttons.module.scss'
import type { Unit } from '@zcode/bizos'
import type { Origin } from '@zcode/nr-utils'
import Listable from '@zcode/nr/es/shared/bizos/listable'
import Listing from '@zcode/nr/es/shared/bizos/listing'
import Pin from '@zcode/nr/es/shared/bizos/pin'
import type { RouteComponentProps } from '@zcode/router-utils'
import type {
  Building,
  FloorplanListing,
  ListableBuilding,
  Listable as ListableType,
  Listing as ListingType,
  NormalListingData,
  Pin as PinType,
  Property,
  RawPinInventorySearch,
} from '@zcode/state-type'
import cn from 'classnames'
import { Fragment, type MouseEventHandler, type ReactNode, useCallback, useState } from 'react'
import { getSavedCustomMessage } from '../../../features/SavedCustomMessage/module/thunks/getSavedCustomMessage'
import { useDispatch } from '../../../modules/useDispatch'
import { useAddNotification } from '../../Notification/NotificationProvider'
import { useGetCommonEntityData } from '../../main/Rail/Listables/ListingCard/helpers/useGetCommonEntityData'
import { useHasInstantTourBooking } from '../../message/MessageForm/TourOptions/useHasInstantTourBooking'
import { getOneTapTourData } from './getOneTapTourData'
import * as styles from './style'

function takesOnly1Parameter(
  fn: NonNullable<TourButtonProps['children']>
): fn is (isMessaged: boolean) => React.ReactNode {
  return fn.length < 2
}

export interface TourButtonProps extends RouteComponentProps {
  bedroomsArray: readonly boolean[]
  btnCss?: Interpolation<Theme>
  buttonCustomStyle?: string
  children?:
    | ((isMessaged: boolean) => ReactNode)
    | ((isMessaged: boolean, hasInstantTourAvailability: boolean) => ReactNode)
  dataTestId?: string
  disableOneTap?: boolean
  entity: ListableType | Property | PinType<RawPinInventorySearch> | null | undefined
  fetchMessagingEntity: (
    listable: ListingType | ListableBuilding | PinType<RawPinInventorySearch>
  ) => Promise<
    ListingType<NormalListingData> | Building | null | undefined | ListingType | ListableBuilding
  >
  getHasInstantTourAvailability: (
    entity: ListableType | Property | null | undefined
  ) => Promise<boolean>
  isExternal: boolean | undefined
  isFetchingAnyTourIntegration: boolean
  isFetchingThisTourIntegration: boolean
  isMessageable: boolean | undefined
  isMessaged: boolean
  isSendingTour: boolean
  isTourDisabled: boolean | undefined
  onClick: (params: {
    hasInstantTourAvailability: boolean
    messageEntity: ListableType | Property | null | undefined
  }) => void
  origin: Origin
  extraPayload?: { currentTab?: string }
  secondaryButtonVariant?: boolean
  selectedFloorplan?: FloorplanListing
  selectedUnit?: Unit
  setSendingTour: (sendingTour: boolean) => void
  size?: string
  submitTour: (data: NonNullable<ReturnType<ReturnType<typeof getOneTapTourData>>>) => Promise<void>
  trackTourClick: () => void
  trackUndoClick: () => void
}

export const TourButtonListCard = ({
  bedroomsArray,
  // btnCss,
  buttonCustomStyle,
  children,
  dataTestId,
  disableOneTap,
  entity,
  fetchMessagingEntity,
  getHasInstantTourAvailability,
  isFetchingAnyTourIntegration,
  // isFetchingThisTourIntegration,
  isExternal,
  isMessageable,
  isMessaged,
  isSendingTour,
  isTourDisabled,
  onClick,
  origin,
  // secondaryButtonVariant = false,
  selectedFloorplan,
  selectedUnit,
  setSendingTour,
  size,
  submitTour,
  trackTourClick,
  trackUndoClick,
}: TourButtonProps) => {
  const addNotification = useAddNotification()
  const [oneTapLoading, setOneTapLoading] = useState(false)
  const dispatch = useDispatch()
  const { hasInstantTourAvailability } = useHasInstantTourBooking(
    entity,
    // don't bother fetching if there are no children to tell about it
    !children || takesOnly1Parameter(children)
  )
  const getCommonEntityData = useGetCommonEntityData()
  const { fullAddress } = getCommonEntityData({
    entity: entity as ListableType,
    addCountryInAddress: true,
  })
  const onClickSubmitTourOrOpenForm: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (event) => {
      event.preventDefault()
      event.stopPropagation()
      setOneTapLoading(true)
      trackTourClick()
      const shouldFetchEntity =
        (isListing(entity) && !entity.hasNormalListingData) ||
        (entity instanceof Listable && entity.isBuilding) ||
        isPin(entity)
      const messageEntity = shouldFetchEntity ? await fetchMessagingEntity(entity) : entity
      const hasInstantTourAvailability = await getHasInstantTourAvailability(messageEntity)
      const oneTapTourData =
        !disableOneTap &&
        dispatch(
          getOneTapTourData({
            bedroomsArray,
            entity: messageEntity,
            hasInstantTourAvailability,
            origin,
            selectedFloorplan,
            selectedUnit,
          })
        )

      const isOneTapElegible = !!oneTapTourData && !messageEntity?.isSpecialtyHousing
      if (!isOneTapElegible) {
        setOneTapLoading(false)
        return onClick({ hasInstantTourAvailability, messageEntity })
      }
      setSendingTour(true)
      let undone = false
      const hasPhone = !!oneTapTourData.formValues.phone

      const hideNotificationEarly = addNotification({
        message: 'Requesting a tour',
        bodyText: hasPhone
          ? `By providing your phone number, you consent (not a condition of purchase) to be called and texted by ${
              entity &&
              ('buildingName' in entity && !!entity.buildingName
                ? entity.buildingName
                : 'name' in entity && !!entity.name
                  ? entity.name
                  : fullAddress)
            }.`
          : undefined,
        onHide: async () => {
          if (undone) {
            return
          }
          const savedCustomMessage = dispatch(getSavedCustomMessage())
          if (savedCustomMessage) {
            oneTapTourData.formValues.message = savedCustomMessage
          }
          await submitTour(oneTapTourData)
          setOneTapLoading(false)
          setSendingTour(false)
        },
        action: {
          label: 'Undo',
          onClick: () => {
            undone = true
            hideNotificationEarly()
            trackUndoClick()
            setOneTapLoading(false)
            setSendingTour(false)
            onClick({ hasInstantTourAvailability, messageEntity })
          },
        },
        shouldTruncate: true,
      })
    },
    [
      addNotification,
      bedroomsArray,
      disableOneTap,
      dispatch,
      entity,
      fetchMessagingEntity,
      getHasInstantTourAvailability,
      trackTourClick,
      trackUndoClick,
      onClick,
      origin,
      selectedFloorplan,
      selectedUnit,
      setSendingTour,
      submitTour,
    ]
  )
  if (!isMessageable || isExternal) {
    return null
  }

  const type = isTourDisabled ? 'info' : 'a tour'
  const typeMessaged = isTourDisabled ? 'Info' : 'Tours'
  const sizeCss =
    size === 'lg'
      ? buttonCss.lgButton
      : size === 'md'
        ? buttonCss.mdButton
        : size === 'sm'
          ? buttonCss.smButton
          : size === 'xs'
            ? buttonCss.xsButton
            : undefined

  return (
    <button
      className={cn(sizeCss, buttonCss.clearButton, buttonCustomStyle)}
      data-test-id={dataTestId}
      // TODO: not needed for listing card
      //   css={btnCss}
      disabled={isMessaged || isSendingTour || isFetchingAnyTourIntegration}
      // TODO: not needed for listing card
      // isLoading={isFetchingThisTourIntegration || oneTapLoading}
      onClick={onClickSubmitTourOrOpenForm}
      type="button"
      // TODO: not needed for listing card
      //   variant={buttonProps?.variant ?? (secondaryButtonVariant ? 'secondary' : undefined)}
    >
      {(children &&
        (takesOnly1Parameter(children)
          ? children(isMessaged)
          : typeof hasInstantTourAvailability === 'boolean' &&
            children(isMessaged, hasInstantTourAvailability))) || (
        <Fragment>
          <span css={styles.text}>
            {isMessaged ? `${typeMessaged} Requested` : `Request ${type}`}
          </span>
          <span css={styles.smallText}>{isMessaged ? 'Requested' : typeMessaged}</span>
        </Fragment>
      )}
    </button>
  )
}

function isListing(entity: TourButtonProps['entity']): entity is ListingType {
  return entity instanceof Listing
}

function isPin(entity: TourButtonProps['entity']): entity is PinType<RawPinInventorySearch> {
  return entity instanceof Pin
}
